import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { PropTypes } from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";
import JobActions from "~/actions/job_actions";
import CircularScoreProgress from "~/components/common/CircularScoreProgress";
import Tooltipable from "~/components/effects/tooltipable";
import JobStore from "~/stores/job_store";

// Pagination size optimized for drawer layout
const DRAWER_JOB_PAGE_SIZE = 5;

const LeadDrawerJobs = ({ leadId }) => {
  const [state, setState] = useState({
    leadID: leadId,
    isOpen: false,
    job_applications: [],
    pagination: { total_pages: 1, current_page: 1 },
    loading: false,
    lastJobStoreAction: null,
  });

  const {
    job_applications = [],
    pagination = { total_pages: 1, current_page: 1 },
    isOpen,
    loading,
  } = state;

  const { current_page, total_pages } = pagination;
  const { leadID } = state;

  const loadJobs = useCallback(
    (page = 1) => {
      setState((prevState) => ({
        ...prevState,
        loading: true,
      }));

      const options = {
        page,
        per: DRAWER_JOB_PAGE_SIZE,
        s: {
          lead_id: leadID,
        },
        job_application_sort: "applied_at-desc",
      };

      JobActions.loadLeadJobApplications(options);
    },
    [leadID],
  );

  const onChange = useCallback(() => {
    const newState = JobStore.getState();
    const {
      lastJobStoreAction,
      leadJobApplications,
      pagination: jobPagination,
    } = newState;

    if (lastJobStoreAction === "loadLeadJobApplicationsDone") {
      setState((prevState) => ({
        ...prevState,
        job_applications: leadJobApplications || [],
        pagination: jobPagination || { total_pages: 1, current_page: 1 },
        loading: false,
        lastJobStoreAction,
      }));
    } else if (lastJobStoreAction === "loadLeadJobApplicationsFail") {
      setState((prevState) => ({
        ...prevState,
        job_applications: [],
        loading: false,
        lastJobStoreAction,
      }));
    }
  }, []);

  useEffect(() => {
    const storeListener = JobStore.addListener(onChange);

    return () => {
      storeListener.remove();
    };
  }, [onChange]);

  useEffect(() => {
    if (isOpen) {
      loadJobs();
    }
  }, [isOpen, loadJobs]);

  const onPageChange = (data) => {
    const newPage = data.selected + 1;
    loadJobs(newPage);
  };

  const onCollapseClick = (e) => {
    e.preventDefault();

    if (!isOpen) {
      loadJobs();
    }
    setState((prevState) => ({
      ...prevState,
      isOpen: !prevState.isOpen,
    }));
  };

  const renderJob = useCallback(
    (job_application, isFirst = false) => {
      const { applied_at, job, candidate_score, source_type } = job_application;
      const { title, status, location } = job;
      const formattedDate = applied_at
        ? new Date(applied_at).toLocaleDateString("en-US")
        : "N/A";

      return (
        <tr key={`job-${job_application.id}`}>
          <td className={classNames("px-0", { "no-border": isFirst })}>
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="d-flex">
                  <strong className="text-nowrap min-width-100">
                    Date Applied:
                  </strong>
                  <span className="ml-2">{formattedDate}</span>
                </div>
                <div className="d-flex">
                  <strong className="text-nowrap min-width-100">
                    Job Name:
                  </strong>
                  <span className="ml-2">{title || "Untitled Job"}</span>
                </div>
                <div className="d-flex">
                  <strong className="text-nowrap min-width-100">
                    Job Status:
                  </strong>
                  <span className="ml-2">{status?.name || "Unknown"}</span>
                </div>
                <div className="d-flex">
                  <strong className="text-nowrap min-width-100">
                    Location:
                  </strong>
                  <span className="ml-2">{location || "No Location"}</span>
                </div>
              </div>

              <div className="d-flex flex-column align-items-center">
                {Rails.abilities.manageLead && (
                  <div className="d-flex flex-row align-items-center gap-2 mb-2">
                    <Tooltipable text="Edit Job">
                      <Link
                        to={`/recruiter/jobs/${job.id}/edit`}
                        className="btn btn-secondary btn-sm mr-1"
                      >
                        <FontAwesomeIcon icon={["far", "edit"]} />
                      </Link>
                    </Tooltipable>
                    <Link
                      to={`/recruiter/jobs/${job.id}/candidates`}
                      className="btn btn-primary btn-sm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Job
                    </Link>
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-center">
                  <CircularScoreProgress
                    score={candidate_score}
                    sourceType={source_type}
                    size={60}
                    strokeWidth={5}
                    animate={isOpen}
                  />
                </div>
              </div>
            </div>
          </td>
        </tr>
      );
    },
    [isOpen],
  );

  return (
    <div>
      <div className="pull-right">
        <button
          type="button"
          className="btn btn-secondary btn-sm mr-1"
          onClick={onCollapseClick}
          disabled={loading}
        >
          {isOpen ? (
            <FontAwesomeIcon icon={["far", "minus"]} />
          ) : (
            <FontAwesomeIcon icon={["far", "plus"]} />
          )}
        </button>
      </div>

      <h4 className={classNames({ mb15: !isOpen })}>Jobs</h4>
      {loading && <p>Loading jobs...</p>}

      <Collapse in={isOpen}>
        <div>
          {!loading && job_applications.length === 0 && <p>No Jobs</p>}
          {!loading && job_applications.length > 0 && (
            <div className="table-responsive">
              <table className={classNames("table")}>
                <tbody>
                  {job_applications.map((job_application, index) =>
                    renderJob(job_application, index === 0),
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="row">
            {total_pages > 1 && (
              <div className="col-6 pl-0 mb-2">
                <nav>
                  <ReactPaginate
                    forcePage={current_page - 1}
                    pageCount={total_pages}
                    pageRangeDisplayed={1}
                    marginPagesDisplayed={2}
                    containerClassName="pagination"
                    activeClassName="active"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    onPageChange={onPageChange}
                    previousLabel={
                      <FontAwesomeIcon icon={["far", "chevron-left"]} />
                    }
                    nextLabel={
                      <FontAwesomeIcon icon={["far", "chevron-right"]} />
                    }
                  />
                </nav>
              </div>
            )}

            {Rails.abilities.manageLead && (
              <div className="col-6">
                <Link
                  to="/recruiter/jobs"
                  className="d-flex align-items-center h-100"
                >
                  View All Jobs
                </Link>
              </div>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

LeadDrawerJobs.propTypes = {
  leadId: PropTypes.number.isRequired,
};

export default LeadDrawerJobs;
